import { useState } from "react";
import { AlertTriangle, AlertOctagon, Info, ShieldAlert, ExternalLink, Copy, Check } from "lucide-react";
import { clsx } from "clsx";
import type { RiskNote } from "@erc/shared";
import { RISK_TYPE_LABELS } from "@erc/shared";

function usptoSearchUrl(term: string): string {
  return `https://tmsearch.uspto.gov/search/search-information?query=${encodeURIComponent(term)}`;
}

interface RiskNotesProps {
  risks: RiskNote[];
}

const severityConfig = {
  low: {
    icon: Info,
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    text: "text-yellow-800",
    iconColor: "text-yellow-500",
    label: "Düşük Risk",
  },
  medium: {
    icon: AlertTriangle,
    bg: "bg-orange-50",
    border: "border-orange-200",
    text: "text-orange-800",
    iconColor: "text-orange-500",
    label: "Orta Risk",
  },
  high: {
    icon: AlertOctagon,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-800",
    iconColor: "text-red-500",
    label: "Yüksek Risk",
  },
};

export function RiskNotes({ risks }: RiskNotesProps) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopied(text);
    setTimeout(() => setCopied((c) => (c === text ? null : c)), 1500);
  };

  if (risks.length === 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
        <ShieldAlert className="h-4 w-4 text-green-600" />
        <p className="text-sm text-green-700">
          Risk tespiti yapılmadı. Tasarım süreci için uygun görünüyor.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {risks.map((risk, index) => {
        const config = severityConfig[risk.severity] ?? severityConfig.low;
        const Icon = config.icon;

        return (
          <div
            key={index}
            className={clsx(
              "rounded-lg border p-4",
              config.bg,
              config.border
            )}
          >
            <div className="flex items-start gap-3">
              <Icon className={clsx("mt-0.5 h-4 w-4 flex-shrink-0", config.iconColor)} />
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <span className={clsx("text-sm font-semibold", config.text)}>
                    {RISK_TYPE_LABELS[risk.type] ?? risk.type}
                  </span>
                  <span
                    className={clsx(
                      "rounded-full px-2 py-0.5 text-xs font-medium",
                      config.bg,
                      config.text,
                      "border",
                      config.border
                    )}
                  >
                    {config.label}
                  </span>
                </div>
                <p className={clsx("text-sm", config.text)}>{risk.message}</p>
                <p className="mt-1.5 text-xs text-gray-600">
                  <span className="font-medium">Öneri: </span>
                  {risk.suggestion}
                </p>
                {risk.term && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-gray-500">İlgili ifade:</span>
                    <code className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-800">{risk.term}</code>
                    <button
                      type="button"
                      onClick={() => copy(risk.term!)}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      {copied === risk.term ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
                      {copied === risk.term ? "Kopyalandı" : "Kopyala"}
                    </button>
                    <a
                      href={usptoSearchUrl(risk.term)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => copy(risk.term!)}
                      className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      title="Kelimeyi kopyalar ve USPTO marka aramasını açar"
                    >
                      <ExternalLink className="h-3 w-3" /> USPTO'da sorgula
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
